/**
 * Survey Verification Button Component
 * =====================================
 * Button that shows pending verification count and opens CANONICAL panel
 * 
 * Updated to use canonical survey grouping:
 * - Shows count of canonical surveys (not individual occurrences)
 * - Opens CanonicalSurveyPanel instead of flat list
 */

import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import CanonicalSurveyPanel from './CanonicalSurveyPanel';
import axios from 'axios';

const SurveyVerificationButton = forwardRef(({ caseId, variant = 'default', onVerificationComplete }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [totalSurveys, setTotalSurveys] = useState(0);
  const [loading, setLoading] = useState(false);

  // Expose openPanel method to parent via ref
  useImperativeHandle(ref, () => ({
    openPanel: () => setIsOpen(true)
  }));

  // Fetch canonical survey summary
  useEffect(() => {
    if (caseId) {
      fetchCanonicalSummary();
    }
  }, [caseId]);

  const fetchCanonicalSummary = async () => {
    setLoading(true);
    try {
      // Try canonical endpoint first
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/survey-verification/case/${caseId}/canonical`
      );
      const summary = response.data.summary || {};
      setPendingCount(summary.pending || 0);
      setTotalSurveys(summary.total_canonical_surveys || 0);
    } catch (error) {
      // Fallback to old summary endpoint
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/survey-verification/case/${caseId}/summary`
        );
        setPendingCount(response.data.pending || 0);
        setTotalSurveys(response.data.total_surveys || 0);
      } catch (e) {
        console.log('Survey verification API not available');
      }
    }
    setLoading(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    // Refresh count when panel closes
    fetchCanonicalSummary();
    // Call completion callback if provided
    if (onVerificationComplete) {
      onVerificationComplete();
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size="sm"
        onClick={() => setIsOpen(true)}
        className="relative"
      >
        {pendingCount > 0 ? (
          <AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
        ) : (
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
        )}
        Verify Surveys
        {totalSurveys > 0 && (
          <Badge 
            className={`ml-2 ${pendingCount > 0 ? 'bg-yellow-500' : 'bg-green-500'} text-white`}
            variant="secondary"
          >
            {pendingCount > 0 ? `${pendingCount}/${totalSurveys}` : `${totalSurveys} ✓`}
          </Badge>
        )}
      </Button>

      <CanonicalSurveyPanel
        caseId={caseId}
        isOpen={isOpen}
        onClose={handleClose}
      />
    </>
  );
});

export default SurveyVerificationButton;
